import { FC, Fragment, ReactNode, useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	LinearProgress,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useQuery } from "@tanstack/react-query";
import { useUserStore } from "../../../../../stores/user.store";
import {
	useCurrencySetting,
	useSettings,
} from "../../../../../hooks/useSetting";
import { useDateFormat } from "../../../../../hooks/useDateFormat";
import { useActivePayers } from "../../../../../hooks/userPayers";
import { useGroupLabels } from "../../../../../hooks/groupLabels";
import { useGetProviderSitesByOUId } from "../../../../../hooks/useBeneficiaries";
import useVisitDetail from "../../../../../hooks/useVisitDetail";
import {
	VisitAnalyteResponse,
	VisitDetailFilters,
	VisitDetailRow,
	reportsService,
} from "../../../../../services/reports.service";
import { triggerDownload } from "../../../../../utils/xlsx-export/build-workbook";
import { buildVisitDetailWorkbook } from "../../../../../utils/xlsx-export/visit-detail-workbook";
import { useAppTheme } from "../../../../../utils/theme";
import { getContrastText } from "../../../../../utils/colorUtils";
import { HeadCell } from "../../../../../components/DataTable/dataTable.interface";
import BasicDatepicker from "../../../../../components/BasicDatepicker/BasicDatepicker";
import {
	NoProgramCard,
	ExclusionBanner,
	OrgUnitFilterField,
	ProgramFilterField,
	ReportActions,
	ReportTabProps,
	groupLabelsForSites,
	payersForPrograms,
	programNameOf,
} from "../common";
import {
	combinedOrgUnitIds,
	hiddenProgramCount,
} from "../../../../../utils/programSelection";

interface Option {
	id: string;
	name: string | null;
}

const toOptions = (
	list: ReadonlyArray<{ _id?: string; id?: string; name?: string | null }>,
): Option[] =>
	(list ?? []).map((o) => ({ id: o._id ?? o.id ?? "", name: o.name ?? null }));

const HEAD: HeadCell[] = [
	{
		id: "visitDate",
		label: "Visit Date",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "member",
		label: "Member Name",
		isSortable: false,
		disablePadding: false,
	},
	{ id: "cin", label: "CIN", isSortable: false, disablePadding: false },
	{
		id: "orgUnit",
		label: "Org Unit",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "provider",
		label: "Provider",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "eventName",
		label: "Event Name",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "visitNumber",
		label: "Visit Number",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "result",
		label: "Visit Result",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "earned",
		label: "Incentive Earned",
		isSortable: false,
		disablePadding: false,
		align: "right",
	},
	{
		id: "payerPlanName",
		label: "Payer Plan",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "payerPlanNumber",
		label: "Payer Plan ID",
		isSortable: false,
		disablePadding: false,
	},
	// SIMS-8078 (AC1): the Program column, and SIMS-8081 (AC1) the Enrollment
	// Status column, are APPENDED here rather than inserted near the front.
	//
	// The Hi-Fi draws a 9-column table that drops Provider, Visit Number, Payer
	// Plan and Payer Plan ID and renames three more. The developer ruled at
	// Inception (kickoff Q7/Q8) that the shipped column set and order stand
	// unchanged and the two new columns are appended — so no existing column is
	// removed, renamed or reordered. `Actions` stays last: it renders the
	// "See Details" toggle that opens the analyte drill-down, which the Hi-Fi
	// omits entirely. The full contract, including the eight accepted deviations
	// from the design, is in
	// aidlc-docs/SIMS-7989/inception/sources/column-contract.md.
	{
		id: "programName",
		label: "Program",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "details",
		label: "Actions",
		isSortable: false,
		disablePadding: false,
		align: "right",
	},
];

/**
 * SIMS-7989 — per-column widths, in HEAD's exact order.
 *
 * The table is `tableLayout: "fixed"`, so these ARE the layout. Any column
 * without an entry here only receives leftover width, and because the sum below
 * is also the table's `minWidth` there is no leftover — an unlisted column
 * collapses to ~0 and paints over its neighbours. Keep this array and HEAD the
 * same length; `VisitDetailTab.test.tsx` asserts it.
 *
 * Program is the widest of the appended columns on purpose: it renders full
 * program names, never the design's abbreviations.
 */
const COL_WIDTHS: number[] = [
	95, // Visit Date         — measured need 72 ("03/14/2026")
	170, // Member Name        — measured need 153; was truncating at 150
	95, // CIN                — measured need 70
	150, // Org Unit           — measured need 136; was truncating at 130
	150, // Provider           — measured need 142; was truncating at 120
	130, // Event Name         — measured need 115 ("Group attendance")
	80, // Visit Number       — measured need 56 (a short integer)
	120, // Visit Result       — pill
	120, // Incentive Earned   — right-aligned currency
	110, // Payer Plan
	110, // Payer Plan ID
	150, // Program            (SIMS-8078) — pill, full name, ellipsized
	100, // Actions            — right-aligned "See Details"
];

/**
 * The table never renders narrower than the sum of its columns; below this the
 * card scrolls horizontally instead of compressing cells into each other.
 * Derived rather than hard-coded, so adding a column cannot silently starve the
 * tail again.
 */
const TABLE_MIN_WIDTH = COL_WIDTHS.reduce((a, b) => a + b, 0);

interface SelectedVisit {
	enrollmentId: string;
	udtResultId: string;
	member: string | null;
	cin: string | null;
	eventName: string | null;
	visitDate: string | null;
	result: string | null;
	// SIMS-7984: carried so the drill-down card's "Visit Result" chip keeps the
	// configured colour. Analyte-level results (Positive/Negative) are synthetic
	// labels with no configuration, so they fall back to the theme styling.
	resultColor: string | null;
	payerPlanName: string | null;
}

const VisitDetailTab: FC<ReportTabProps> = ({
	programId,
	programIds,
	programs,
	onProgramChange,
	onProgramsChange,
}) => {
	const theme = useAppTheme();
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const currencyMask = useCurrencySetting()?.mask ?? "$";
	// SIMS-7770: date display/entry follows the account's country format setting
	// (US → MM/DD/YYYY, UK → DD/MM/YYYY); JANUS deploys in both.
	const { dateFormat } = useDateFormat();
	const programName = programNameOf(programs, programId);
	// SIMS-8104 (AC3): the names of every SELECTED program, for the export's
	// program list and its filename. Derived from the selection rather than from
	// the returned rows, so a selected program with no visits in range is still
	// named in the file's own statement of scope.
	const selectedProgramNames = (programIds ?? [])
		.map((id) => programNameOf(programs, id))
		.filter((n): n is string => !!n);

	const [orgUnitIds, setOrgUnitIds] = useState<string[]>([]);
	const [payerIds, setPayerIds] = useState<string[]>([]);
	const [groupLabelIds, setGroupLabelIds] = useState<string[]>([]);
	const [providerId, setProviderId] = useState<string>("");
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");
	const [cinInput, setCinInput] = useState<string>("");
	const [cin, setCin] = useState<string>("");
	const [pageNumber, setPageNumber] = useState<number>(0);
	const [pageSize] = useState<number>(25);
	const [selected, setSelected] = useState<SelectedVisit | null>(null);
	// Which analyte row is highlighted within the drill-down — drives the
	// analyte fields shown in the detail card. Resets when the visit changes.
	const [analyteIdx, setAnalyteIdx] = useState<number | null>(null);

	// Brand info (Settings → brand information) is used by the PDF export
	// footer. Generated date stamps the on-screen header and the PDF.
	const { data: settings } = useSettings();
	const brand = settings?.brandInformation;
	const generated = dayjs().format(dateFormat);

	// Design tokens → theme palette (driven by the account's themeColors).
	const c = {
		cardBg: theme.palette.surface.light,
		band: theme.palette.surface.main,
		border: theme.palette.outline.light,
		divider: theme.palette.outline.light,
		label: theme.palette.textSecondary.main,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		accent: theme.palette.primary.main,
		accentBand: theme.palette.surface.dark,
		bandDark: theme.palette.primary.dark, // --sims-purple-900 (title band)
		onDark: theme.palette.surface.light, // text on the band
		teal: theme.palette.secondary.main, // --sims-teal-500 (section rule)
		posBg: theme.palette.error.light, // highlighted POSITIVE analyte row
		posInk: theme.palette.error.dark,
		negInk: theme.palette.secondary.dark,
		// SIMS-8081: the Hi-Fi renders the ACTIVE enrollment-status pill as
		// brand-secondary-dark text on brand-secondary-light. Every other status it
		// draws is neutral (muted ink on the band surface).
		okBg: theme.palette.secondary.light,
	};

	const hexToRgb = (hex: string): [number, number, number] => {
		const h = (hex || "").replace("#", "");
		const s =
			h.length === 3
				? h
						.split("")
						.map((x) => x + x)
						.join("")
				: h;
		const n = parseInt(s || "000000", 16);
		return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
	};

	// SIMS-7595: the OU ids the user is actually assigned for this program. Used
	// both to scope the OU dropdown and to constrain the report to the user's OUs
	// when no explicit OU is selected, so rows never include unassigned OUs.
	// SIMS-7990: across ALL selected programs, de-duplicated by org-unit id — a single
	// programId kept the options (and the shared group) limited to one program.
	const assignedOuIds = combinedOrgUnitIds(
		userInfo?.programs,
		programIds ?? [],
	);

	/**
	 * Programs excluded by the org-unit filter (SIMS-8006 AC1 / FR-5) — derived from
	 * the selection, no API support needed. Only counted when an explicit org-unit
	 * filter is applied; without one every selected program is in scope.
	 */
	const excludedProgramCount = (programIds ?? []).filter((pid) => {
		if (orgUnitIds.length === 0) return false;
		const ous = combinedOrgUnitIds(userInfo?.programs, [pid]);
		return !ous.some((ou) => orgUnitIds.includes(ou));
	}).length;

	/**
	 * SIMS-8023: the report runs only on "Run report" — `applied` is the snapshot
	 * that was run, and the query reads it instead of the live filter state.
	 *
	 * Pagination is deliberately NOT part of the snapshot (AC4): paging is not a
	 * filter change, so it re-queries the applied filters at the new page and keeps
	 * the results, while editing a filter clears them (AC3).
	 */
	// SIMS-8006: the Hi-Fi's "View programs" affordance on the exclusion banner.
	// Scrolls back to the filter bar, where the chips show which programs are
	// selected — the banner stays cause-neutral and must not name the hidden ones.
	const filterBarRef = useRef<HTMLDivElement>(null);
	const viewPrograms = (): void =>
		filterBarRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});

	const [applied, setApplied] = useState<{
		// SIMS-7990: EVERY selected program, not the first. Serialized to CSV only at
		// the request boundary — see the query hook below.
		programIds: string[];
		orgUnitIds?: string;
		payerIds?: string;
		groupLabelIds?: string;
		providerId?: string;
		startDate?: string;
		endDate?: string;
		cin?: string;
	} | null>(null);

	const runReport = (): void => {
		/**
		 * SIMS-8901 — run the CIN that is IN THE BOX, not the last committed one.
		 *
		 * Every other filter is a picker whose value is committed the moment it
		 * changes. CIN is free text, committed to `cin` only by blur or Enter
		 * (`applyCin`), so the run depended on a focus change happening first. Type
		 * a CIN, press Run report, and whether the filter applied came down to
		 * whether the click blurred the field in time — QA saw the whole PTM
		 * Analytes set (47 rows) come back for a single-participant search, and the
		 * captured request carried no `cin` parameter at all.
		 *
		 * Reading `cinInput` here makes "what the field shows" and "what ran" the
		 * same thing by construction, for a real click, a keyboard-only run and an
		 * automated one alike. `setCin` keeps the committed state in step so the
		 * header/export summaries agree.
		 */
		const pendingCin = cinInput.trim();
		if (pendingCin !== cin) setCin(pendingCin);
		setPageNumber(0);
		setApplied({
			programIds: programIds ?? [],
			orgUnitIds:
				(orgUnitIds.length ? orgUnitIds : assignedOuIds).join(",") || undefined,
			payerIds: payerIds.length ? payerIds.join(",") : undefined,
			groupLabelIds: groupLabelIds.length ? groupLabelIds.join(",") : undefined,
			providerId: providerId || undefined,
			startDate: startDate || undefined,
			endDate: endDate || undefined,
			cin: pendingCin || undefined,
		});
	};

	/**
	 * SIMS-8023 AC3 — ANY filter edit invalidates the run.
	 *
	 * This tab cleared the results from "Clear all" alone: every filter, including the
	 * org units, only reset the page number, so the previous run's rows stayed on
	 * screen under filters that no longer described them (TestRail C15441). Each setter
	 * now goes through a handler that resets the page AND drops the snapshot.
	 */
	const clearResults = (): void => setApplied(null);

	const changeOrgUnitIds = (v: string[]): void => {
		setOrgUnitIds(v);
		setPageNumber(0);
		clearResults();
	};
	const changePayerIds = (v: string[]): void => {
		setPayerIds(v);
		setPageNumber(0);
		clearResults();
	};
	const changeGroupLabelIds = (v: string[]): void => {
		setGroupLabelIds(v);
		setPageNumber(0);
		clearResults();
	};
	const changeProviderId = (v: string): void => {
		setProviderId(v);
		setPageNumber(0);
		clearResults();
	};
	const changeStartDate = (v: string): void => {
		setStartDate(v);
		setPageNumber(0);
		clearResults();
	};
	const changeEndDate = (v: string): void => {
		setEndDate(v);
		setPageNumber(0);
		clearResults();
	};

	/**
	 * SIMS-8023 AC3 (Edge) — changing the PROGRAM selection clears the results too.
	 *
	 * Keyed on the joined ids, not the array: the dashboard hands down
	 * `programByTab[id] ?? []`, so an absent entry is a new array every render and an
	 * identity dependency would clear in a loop (TestRail C15442).
	 */
	const programKey = (programIds ?? []).join(",");
	useEffect(() => {
		setApplied(null);
	}, [programKey]);

	const { data, isLoading, isFetching } = useVisitDetail({
		accountId,
		// Empty until Run — this is what keeps the query from firing.
		// CSV boundary (SIMS-7990) — one id or a comma-separated list on the same
		// param. Empty selection joins to "", keeping the query disabled.
		programId: (applied?.programIds ?? []).join(","),
		orgUnitIds: applied?.orgUnitIds,
		payerIds: applied?.payerIds,
		groupLabelIds: applied?.groupLabelIds,
		providerId: applied?.providerId,
		startDate: applied?.startDate,
		endDate: applied?.endDate,
		cin: applied?.cin,
		pageNumber,
		pageSize,
	});

	// Hide the analyte drill-down when the expanded visit leaves the results.
	useEffect(() => {
		if (
			selected &&
			!(data?.rows ?? []).some((r) => r.udtResultId === selected.udtResultId)
		) {
			setSelected(null);
		}
	}, [data, selected]);

	// Clear the analyte selection whenever the visit changes (card falls back to
	// the visit-level result until a new analyte row is clicked).
	useEffect(() => {
		setAnalyteIdx(null);
	}, [selected?.udtResultId]);

	const { data: payers } = useActivePayers();
	const { data: groupLabels } = useGroupLabels();
	// SIMS-7397: group labels are scoped to the OUs the user has for the selected
	// program (same logic as the Beneficiary Report).
	const { data: providerSites } = useGetProviderSitesByOUId({
		ouIds: assignedOuIds,
		queryKey: "sims-reports-provider-sites",
	});
	// SIMS-7396: the Payer filter lists only payers configured for the selected
	// program (program.payers), intersected with the account's active payers.
	// When the program has none, the dropdown is disabled.
	// SIMS-7994 AC3: the union across EVERY selected program, not the first one
	// (TestRail C15460).
	const selectedPrograms = programs.filter((p) =>
		(programIds ?? []).includes(p._id),
	);
	const payerOptions = toOptions(payersForPrograms(payers, selectedPrograms));
	const payersDisabled = payerOptions.length === 0;
	const groupLabelOptions = toOptions(
		groupLabelsForSites(groupLabels, providerSites),
	);
	// SIMS-7595: OU dropdown lists only the user's assigned OUs (scoped provider
	// sites), not the program-wide data.availableSites which leaks unassigned OUs.
	const siteOptions = toOptions(providerSites ?? []);
	const providerOptions = toOptions(data?.availableProviders ?? []);

	const money = (n: number): string => `${currencyMask}${(n ?? 0).toFixed(2)}`;

	const nameById = (opts: Option[], ids: string[]): string =>
		ids
			.map((id) => opts.find((o) => o.id === id)?.name ?? id)
			.filter(Boolean)
			.join(", ");

	const contextLine = (): string => {
		const dateRange =
			startDate || endDate
				? `${startDate ? dayjs(startDate).format(dateFormat) : "…"} – ${
						endDate ? dayjs(endDate).format(dateFormat) : "…"
					}`
				: "";
		return [
			programName,
			nameById(siteOptions, orgUnitIds),
			nameById(payerOptions, payerIds),
			nameById(groupLabelOptions, groupLabelIds),
			dateRange,
		]
			.filter((s) => s && s.length > 0)
			.join(" · ");
	};

	// All filters by name, with values (or "All"/"Any" when unselected) — shown
	// in the report header so the PDF records exactly what was applied.
	const filterPairs = (): Array<[string, string]> => [
		// SIMS-8104 (AC3): the export's summary line lists every SELECTED program,
		// alphabetically, so the PDF states its own scope instead of leaving the
		// reader to infer it from the rows. Falls back to the single-program label
		// (and then "All") so nothing regresses when one program is selected.
		[
			"Program",
			selectedProgramNames.length > 1
				? [...selectedProgramNames]
						.sort((a, b) => a.localeCompare(b))
						.join(", ")
				: programName || "All",
		],
		["Org units", nameById(siteOptions, orgUnitIds) || "All"],
		["Payer", nameById(payerOptions, payerIds) || "All"],
		["Group labels", nameById(groupLabelOptions, groupLabelIds) || "All"],
		[
			"Provider",
			providerOptions.find((o) => o.id === providerId)?.name || "All",
		],
		["Start date", startDate ? dayjs(startDate).format(dateFormat) : "Any"],
		["End date", endDate ? dayjs(endDate).format(dateFormat) : "Any"],
		["CIN", cin || "All"],
	];

	// SIMS-7984: the chip colour comes from the result's configured colour
	// (EventResult.displayColor, surfaced as row.resultColor). The fallback below
	// only runs for rows with no matched result — "Invalid" (tampered) and
	// "Missed" (flag-driven), which the backend emits as literal labels — or for a
	// result whose configuration has no colour set.
	//
	// Note this used to be the *only* path, matching on the label text, which is
	// why the chips turned grey: SIMS-7770 changed the label from the full display
	// ("UDS - Stimulant Negative ($)", which contained "negative") to the
	// shortName ("Neg.", which matches nothing). A label is presentational and
	// must not drive colour — hence keying off the configured colour instead.
	const resultStyle = (value: string | null): { bg: string; color: string } => {
		const s = (value ?? "").toLowerCase();
		// SIMS-7563: Positive/Unexcused → red
		if (/(unexcused|positive)/.test(s)) {
			return { bg: theme.palette.error.light, color: theme.palette.error.dark };
		}
		// Excused absence → amber
		if (/excus/.test(s)) {
			return {
				bg: theme.palette.warning.light,
				color: theme.palette.warning.dark,
			};
		}
		// Missed visit → blue
		if (/missed/.test(s)) {
			return {
				bg: theme.palette.info.light,
				color: theme.palette.info.dark,
			};
		}
		// Invalid → gray/neutral
		if (/invalid/.test(s)) {
			return { bg: c.band, color: c.muted };
		}
		// Negative/Attended → teal
		if (/(negative|attend)/.test(s)) {
			return {
				bg: theme.palette.secondary.light,
				color: theme.palette.secondary.dark,
			};
		}
		return { bg: c.band, color: c.muted };
	};

	const ResultPill: FC<{ value: string | null; color?: string | null }> = ({
		value,
		color,
	}) => {
		if (!value) return <span>—</span>;
		// A configured colour is used as the chip fill; the text colour is then
		// picked by WCAG relative luminance, because admins configure anything from
		// pastels (#cce8b5) to fully saturated hues (#e61e1e).
		const st = color
			? { bg: color, color: getContrastText(color) }
			: resultStyle(value);
		return (
			<Box
				component="span"
				sx={{
					display: "inline-block",
					bgcolor: st.bg,
					color: st.color,
					fontSize: 13,
					fontWeight: 600,
					px: 1.5,
					py: 0.5,
					borderRadius: "9999px",
				}}
			>
				{value}
			</Box>
		);
	};

	const visitRows = data?.rows ?? [];
	// SIMS-7770 (B1): the Totals row reflects the WHOLE filtered set (server-side
	// sum), not just the current page.
	const earnedTotal = data?.totalEarned ?? 0;

	// Select a visit row → drives the two detail cards (meta + analytes).
	const selectVisit = (r: (typeof visitRows)[number]): void => {
		if (!r.hasAnalyteDetails) return;
		setSelected(
			selected?.udtResultId === r.udtResultId
				? null
				: {
						enrollmentId: r.enrollmentId,
						udtResultId: r.udtResultId,
						member: r.member,
						cin: r.cin,
						eventName: r.eventName,
						visitDate: r.visitDate,
						result: r.result,
						resultColor: r.resultColor,
						payerPlanName: r.payerPlanName,
					},
		);
	};

	// Lazy analyte drill-down for the expanded visit.
	const analyteQuery = useQuery<VisitAnalyteResponse>({
		queryKey: [
			"reports-visit-analytes",
			selected?.enrollmentId ?? "",
			selected?.udtResultId ?? "",
		],
		queryFn: () =>
			selected
				? reportsService.getVisitAnalytes(
						accountId,
						selected.enrollmentId,
						selected.udtResultId,
					)
				: Promise.reject(new Error("no visit selected")),
		enabled: Boolean(selected),
		staleTime: 1000 * 60 * 5,
	});

	// Analyte rows for the drill-down table. Clicking a row selects it and the
	// card's "Visit result" then reflects that analyte (falls back to the visit
	// result when nothing is selected).
	const analytes = analyteQuery.data?.analytes ?? [];
	const selAnalyte = analyteIdx !== null ? analytes[analyteIdx] : undefined;

	const exportFilters = (): VisitDetailFilters => ({
		accountId,
		// SIMS-7990: the export carries EVERY selected program, as CSV — otherwise the
		// workbook would be narrower than the screen it was exported from.
		programId: (programIds ?? []).join(","),
		// SIMS-7595: mirror the on-screen fetch — fall back to the user's assigned
		// OUs when none is explicitly selected, so the Excel/PDF export is scoped
		// to the same OUs as the screen and never leaks unassigned-OU rows.
		orgUnitIds:
			(orgUnitIds.length ? orgUnitIds : assignedOuIds).join(",") || undefined,
		payerIds: payerIds.length ? payerIds.join(",") : undefined,
		groupLabelIds: groupLabelIds.length ? groupLabelIds.join(",") : undefined,
		providerId: providerId || undefined,
		startDate: startDate || undefined,
		endDate: endDate || undefined,
		cin: cin || undefined,
		filterSummary: contextLine(),
	});

	const [exporting, setExporting] = useState<"" | "excel" | "pdf">("");

	// SIMS-7588: build the .xlsx in the browser (exceljs) from JSON data instead
	// of downloading a binary body through API Gateway/CloudFront, which arrived
	// as undecoded base64 and could not be opened in Excel. Mirrors the working
	// consolidated export (build-workbook.ts).
	const handleExcel = async (): Promise<void> => {
		setExporting("excel");
		try {
			const { blob, filename } = await buildVisitDetailWorkbook({
				...exportFilters(),
				programName,
				// SIMS-8104 AC3 — every selected program, for the cover sheet's
				// program list and the N-programs filename.
				programNames: selectedProgramNames,
				// SIMS-8903 — the cover sheet reads the SAME filter pairs the PDF
				// header prints. Previously the workbook received only ids, so the
				// cover fell back to its legacy rows and reported "Researcher filter:
				// All" with a provider applied, omitting Payer, Group labels and CIN.
				// One source for both exports is what stops them disagreeing again.
				filterPairs: filterPairs(),
				currencyMask,
				dateFormat,
			});
			triggerDownload({ blob, filename });
		} finally {
			setExporting("");
		}
	};

	// Redesign (B1) — the PDF follows the same pattern as the screen: purple
	// title band, teal section rule, themed table with colored result cells, and
	// a SIMS footer with the generated date. Colors come from the theme tokens.
	const handlePdf = async (): Promise<void> => {
		setExporting("pdf");
		try {
			const allRows = await reportsService.fetchAllVisitDetail(exportFilters());

			// Generate flattened rows (one per analyte) for visits with cup data
			// Batch-fetches analyte details to avoid N+1; caches results by visitId
			const generateAnalyteDetailRows = async (
				visits: VisitDetailRow[],
			): Promise<
				Array<{
					visitId: string;
					enrollmentId: string;
					visitDate: string | null;
					visitNumber: number | null;
					member: string | null;
					cin: string | null;
					eventName: string | null;
					cupName: string | null;
					analyteShortName: string | null;
					// SIMS-7588 dropped the separate "Scientific Name" column from the
					// analyte exports, collapsing short + scientific into one
					// "Analyte Name". The API returns both (AnalyteResult.scientificName);
					// only the export builder stopped carrying it. Restored.
					analyteScientificName: string | null;
					// Dropped by the same commit. Kept as TWO fields so the export can be
					// filtered on the flag without parsing the reasons text.
					isTampered: string;
					tamperedReasons: string;
					positive: string;
					payerPlanName: string | null;
					payerPlanNumber: string | null;
					// SIMS-8104 AC1 — the analyte sheet carries Program too.
					programName: string | null;
				}>
			> => {
				const rows: Array<{
					visitId: string;
					enrollmentId: string;
					visitDate: string | null;
					visitNumber: number | null;
					member: string | null;
					cin: string | null;
					eventName: string | null;
					cupName: string | null;
					analyteShortName: string | null;
					// SIMS-7588 dropped the separate "Scientific Name" column from the
					// analyte exports, collapsing short + scientific into one
					// "Analyte Name". The API returns both (AnalyteResult.scientificName);
					// only the export builder stopped carrying it. Restored.
					analyteScientificName: string | null;
					// Dropped by the same commit. Kept as TWO fields so the export can be
					// filtered on the flag without parsing the reasons text.
					isTampered: string;
					tamperedReasons: string;
					positive: string;
					payerPlanName: string | null;
					payerPlanNumber: string | null;
					programName: string | null;
				}> = [];

				const analytesCache = new Map<string, VisitAnalyteResponse | null>();

				for (const visit of visits) {
					if (!visit.hasAnalyteDetails) continue;

					const cacheKey = `${visit.enrollmentId}:${visit.udtResultId}`;
					let analyteData = analytesCache.get(cacheKey);

					if (!analyteData) {
						try {
							analyteData = await reportsService.getVisitAnalytes(
								accountId,
								visit.enrollmentId,
								visit.udtResultId,
							);
							analytesCache.set(cacheKey, analyteData);
						} catch {
							analytesCache.set(cacheKey, null);
							continue;
						}
					}

					if (!analyteData || analyteData.analytes.length === 0) continue;

					for (const analyte of analyteData.analytes) {
						rows.push({
							visitId: visit.udtResultId,
							enrollmentId: visit.enrollmentId,
							visitDate: visit.visitDate,
							visitNumber: visit.visitNumber ?? null,
							member: visit.member,
							cin: visit.cin,
							eventName: visit.eventName,
							cupName: analyteData.cupName,
							analyteShortName: analyte.shortName,
							analyteScientificName: analyte.scientificName,
							isTampered: analyteData.isTampered ? "Yes" : "No",
							tamperedReasons: (analyteData.tamperedReasons ?? []).join(", "),
							positive: analyte.positive ? "Positive" : "Negative",
							payerPlanName: visit.payerPlanName ?? null,
							payerPlanNumber: visit.payerPlanNumber ?? null,
							programName: visit.programName ?? null,
						});
					}
				}

				return rows;
			};
			const doc = new jsPDF("landscape", "pt", "a4");
			const pageW = doc.internal.pageSize.getWidth();
			const pageH = doc.internal.pageSize.getHeight();
			const M = 40;
			const purple = hexToRgb(theme.palette.primary.dark);
			const teal = hexToRgb(theme.palette.secondary.main);
			const muted = hexToRgb(theme.palette.textSecondary.main);
			const headBg = hexToRgb(theme.palette.surface.main);
			const posInk = hexToRgb(theme.palette.error.dark);
			const negInk = hexToRgb(theme.palette.secondary.dark);

			// Title band
			doc.setFillColor(purple[0], purple[1], purple[2]);
			doc.roundedRect(M, 34, pageW - M * 2, 44, 5, 5, "F");
			doc.setTextColor(255, 255, 255);
			doc.setFont("helvetica", "bold");
			doc.setFontSize(16);
			doc.text("Visit Detail Report", M + 16, 62);
			doc.setFont("helvetica", "normal");
			doc.setFontSize(9);
			doc.text(`Generated ${generated}`, pageW - M - 16, 62, {
				align: "right",
			});

			// Applied filters — every filter by name, "All"/"Any" when unset.
			let y = 96;
			doc.setFontSize(8);
			const filterLine = filterPairs()
				.map(([k, v]) => `${k}: ${v}`)
				.join("    ");
			const wrapped = doc.splitTextToSize(filterLine, pageW - M * 2);
			doc.setTextColor(muted[0], muted[1], muted[2]);
			doc.text(wrapped, M, y);
			y += wrapped.length * 11 + 6;

			// "Visits" section rule
			doc.setTextColor(purple[0], purple[1], purple[2]);
			doc.setFont("helvetica", "bold");
			doc.setFontSize(12);
			doc.text("Visits", M, y + 4);
			doc.setFont("helvetica", "normal");
			doc.setFontSize(8.5);
			doc.setTextColor(muted[0], muted[1], muted[2]);
			doc.text(`${allRows.length} rows`, pageW - M, y + 4, { align: "right" });
			doc.setDrawColor(teal[0], teal[1], teal[2]);
			doc.setLineWidth(1.5);
			doc.line(M, y + 9, pageW - M, y + 9);
			y += 20;

			autoTable(doc, {
				startY: y,
				theme: "grid",
				margin: { left: M, right: M },
				styles: { fontSize: 8 },
				headStyles: { fillColor: headBg, textColor: purple, fontStyle: "bold" },
				columnStyles: { 9: { halign: "right" } },
				head: [
					[
						"Visit Date",
						"Visit Number",
						"Member Name",
						"CIN",
						"Org Unit",
						"Provider",
						"Event Name",
						"Visit Result",
						"Incentive Earned",
						"Payer Plan",
						"Payer Plan ID",
						// SIMS-8104 AC1: Program on the PDF's Visits section too — AC1 says
						// "Excel OR PDF", so both carry it. Appended, matching the on-screen
						// and .xlsx column order. Enrollment Status was dropped by SIMS-8916.
						"Program",
					],
				],
				body: allRows.map((r) => [
					r.visitDate ? dayjs(r.visitDate).format(dateFormat) : "—",
					r.visitNumber ?? "—",
					r.member ?? "—",
					r.cin ?? "—",
					r.orgUnit ?? "—",
					r.provider ?? "—",
					r.eventName ?? "—",
					r.result ?? "—",
					money(r.earned),
					r.payerPlanName ?? "—",
					r.payerPlanNumber ?? "—",
					r.programName ?? "—",
				]),
				didParseCell: (hook) => {
					if (hook.section === "body" && hook.column.index === 7) {
						// SIMS-7984: fill the cell with the result's configured colour
						// (same source as the on-screen chip) instead of re-deriving it
						// from the label text, which is what turned the chips grey.
						const row = allRows[hook.row.index];
						if (row?.resultColor) {
							hook.cell.styles.fillColor = hexToRgb(row.resultColor);
							hook.cell.styles.textColor = hexToRgb(
								getContrastText(row.resultColor),
							);
							hook.cell.styles.fontStyle = "bold";
							return;
						}
						// No configured colour — only "Invalid" and "Missed" reach here.
						const v = (row?.result ?? "").toLowerCase();
						if (/(unexcused|positive|missed|absent|invalid)/.test(v)) {
							hook.cell.styles.textColor = posInk;
							hook.cell.styles.fontStyle = "bold";
						} else if (/(negative|attend)/.test(v)) {
							hook.cell.styles.textColor = negInk;
							hook.cell.styles.fontStyle = "bold";
						}
					}
				},
			});

			// Sheet 2 — Analyte Detail section (SIMS-5688, SIMS-7570)
			// One row per analyte for visits where cupsAndAnalytes === true AND cup data present
			const analyteRows = await generateAnalyteDetailRows(allRows);
			if (analyteRows.length > 0) {
				// Add page break and new section
				doc.addPage();
				let y2 = 60;

				// "Analytes" section title
				doc.setTextColor(purple[0], purple[1], purple[2]);
				doc.setFont("helvetica", "bold");
				doc.setFontSize(12);
				doc.text("Analytes", M, y2 + 4);
				doc.setFont("helvetica", "normal");
				doc.setFontSize(8.5);
				doc.setTextColor(muted[0], muted[1], muted[2]);
				doc.text(`${analyteRows.length} rows`, pageW - M, y2 + 4, {
					align: "right",
				});
				doc.setDrawColor(teal[0], teal[1], teal[2]);
				doc.setLineWidth(1.5);
				doc.line(M, y2 + 9, pageW - M, y2 + 9);
				y2 += 20;

				autoTable(doc, {
					startY: y2,
					theme: "grid",
					margin: { left: M, right: M },
					styles: { fontSize: 8 },
					headStyles: {
						fillColor: headBg,
						textColor: purple,
						fontStyle: "bold",
					},
					head: [
						[
							"Visit Date",
							"Visit Number",
							"Member Name",
							"CIN",
							"Event Name",
							"Cup Name",
							"Tampered",
							// SIMS-7588 collapsed these two into a single "Analyte Name"
							// holding only the short name. Short name first, scientific name
							// to its right — the order the on-screen panel already uses.
							"Short Name",
							"Scientific Name",
							"Analyte Result",
							"Tampered Reasons",
							"Payer Plan",
							"Payer Plan ID",
							// SIMS-8104 AC1 — Program on the analyte section too, as the
							// Hi-Fi states explicitly. Deliberately NO Enrollment Status
							// here (AC2 puts that on the Visits section only).
							"Program",
						],
					],
					body: analyteRows.map((r) => [
						r.visitDate ? dayjs(r.visitDate).format(dateFormat) : "—",
						r.visitNumber ?? "—",
						r.member ?? "—",
						r.cin ?? "—",
						r.eventName ?? "—",
						r.cupName ?? "—",
						r.isTampered,
						r.analyteShortName ?? "—",
						r.analyteScientificName ?? "—",
						r.positive,
						r.tamperedReasons || "—",
						r.payerPlanName ?? "—",
						r.payerPlanNumber ?? "—",
						r.programName ?? "—",
					]),
					didParseCell: (hook) => {
						if (hook.section === "body" && hook.column.index === 7) {
							const v = (
								analyteRows[hook.row.index]?.positive ?? ""
							).toLowerCase();
							if (v === "positive") {
								hook.cell.styles.textColor = posInk;
								hook.cell.styles.fontStyle = "bold";
							} else if (v === "negative") {
								hook.cell.styles.textColor = negInk;
								hook.cell.styles.fontStyle = "bold";
							}
						}
					},
				});
			}

			// Footer on every page
			const brandLeft = [programName, brand?.title].filter(Boolean).join(" — ");
			const pages = doc.internal.pages.length - 1;
			for (let p = 1; p <= pages; p++) {
				doc.setPage(p);
				doc.setDrawColor(muted[0], muted[1], muted[2]);
				doc.setLineWidth(0.5);
				doc.line(M, pageH - 30, pageW - M, pageH - 30);
				doc.setFontSize(8);
				doc.setFont("helvetica", "normal");
				doc.setTextColor(muted[0], muted[1], muted[2]);
				doc.text(brandLeft, M, pageH - 18);
				doc.setFont("helvetica", "bold");
				doc.setTextColor(purple[0], purple[1], purple[2]);
				// SIMS-7563: the centered footer label comes from the tenant's
				// brand information (settings.brandInformation.title), not a
				// hardcoded product name, so tenant-branded PDFs read correctly.
				// Falls back to "SIMS" when the tenant has no brand title set.
				doc.text(brand?.title || "SIMS", pageW / 2, pageH - 18, {
					align: "center",
				});
				doc.setFont("helvetica", "normal");
				doc.setTextColor(muted[0], muted[1], muted[2]);
				doc.text(`Page ${p} of ${pages}`, pageW - M, pageH - 18, {
					align: "right",
				});
			}

			doc.save("visit_detail.pdf");
		} finally {
			setExporting("");
		}
	};

	const applyCin = (): void => {
		if (cinInput.trim() !== cin) {
			setCin(cinInput.trim());
			setPageNumber(0);
			// SIMS-8023 AC3: CIN is a filter like any other — a committed change
			// invalidates the run. Guarded by the same equality check, so a blur that
			// changes nothing does not wipe results the user is still reading.
			clearResults();
		}
	};

	const total = data?.pagination.total ?? 0;
	const rangeStart = total === 0 ? 0 : pageNumber * pageSize + 1;
	const rangeEnd = Math.min((pageNumber + 1) * pageSize, total);
	// SIMS-8078 (AC1): how many programs contributed to the merged set. Counted
	// from the SELECTION rather than from the returned rows, so the caption still
	// reads correctly when a selected program happens to have no visits in range
	// (that exclusion is reported separately by the Foundation's banner,
	// SIMS-8006 / FR-5).
	const programCount = (programIds ?? []).length;

	// ---- presentational helpers -------------------------------------------
	// LOCAL FilterField, distinct from the one exported by ../common (used by
	// ProgramFilterField / OrgUnitFilterField). This one owns this tab's plain filters.
	const FilterField: FC<{
		label: string;
		badge?: string;
		/** Start a fresh grid row — pins the item to column 1 so the grid wraps. */
		newRow?: boolean;
		/** Muted sub-label under the control, e.g. "Grouped by program". */
		helperText?: string;
		children: ReactNode;
	}> = ({ label, badge, newRow, helperText, children }) => (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				minWidth: 0,
				...(newRow ? { gridColumn: { md: "1" } } : {}),
			}}
		>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography
					sx={{
						fontSize: 12,
						fontWeight: 600,
						letterSpacing: "0.06em",
						color: c.label,
					}}
				>
					{label.toUpperCase()}
				</Typography>
				{badge && (
					<Chip
						size="small"
						label={badge}
						sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
						color="primary"
						variant="outlined"
					/>
				)}
			</Stack>
			{children}
			{/* Always rendered so fields with and without a sub-label stay the same
			    height and their controls keep sharing a baseline. */}
			<Typography sx={{ fontSize: 12, color: c.muted, minHeight: 18 }}>
				{helperText ?? ""}
			</Typography>
		</Box>
	);

	const multiInput = (
		value: string[],
		options: Option[],
		onChange: (v: string[]) => void,
		placeholder: string,
		disabled = false,
	): ReactNode => (
		<Select
			size="small"
			multiple
			displayEmpty
			disabled={disabled}
			value={value}
			input={<OutlinedInput />}
			onChange={(e) => {
				const v = e.target.value;
				onChange(typeof v === "string" ? v.split(",") : v);
				setPageNumber(0);
			}}
			renderValue={(sel) =>
				(sel as string[]).length === 0 ? (
					<Typography sx={{ color: c.muted }}>{placeholder}</Typography>
				) : (
					<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
						{(sel as string[]).map((id) => (
							<Chip
								key={id}
								size="small"
								label={options.find((o) => o.id === id)?.name ?? id}
							/>
						))}
					</Box>
				)
			}
		>
			{options.map((o) => (
				<MenuItem key={o.id} value={o.id}>
					{o.name ?? o.id}
				</MenuItem>
			))}
		</Select>
	);

	const summaryField = (label: string, value: ReactNode): ReactNode => (
		<Box>
			<Typography sx={{ fontSize: 14, color: c.muted, mb: 0.5 }}>
				{label}
			</Typography>
			<Box sx={{ fontSize: 17, color: c.ink }}>{value}</Box>
		</Box>
	);

	const clearFilters = (): void => {
		/**
		 * SIMS-8902 — "Clear all" clears the PROGRAM selection too.
		 *
		 * This used to re-select `programs[0]`, honouring SIMS-7390's invariant that
		 * "a program is always selected". That invariant belongs to the
		 * single-program report: with a multi-select, silently leaving one program
		 * behind means the button does not do what it says, and the user cannot tell
		 * a deliberate one-program selection from a leftover. SUPERSEDES SIMS-7390
		 * for this tab.
		 *
		 * Clearing to EMPTY (rather than to FR-15's default pre-selection) is what
		 * the button's label promises; the report then shows its "select a program"
		 * prompt, which is the same state as a fresh tab with nothing chosen. The
		 * query stays disabled while the selection is empty, so this costs no
		 * request.
		 */
		onProgramsChange?.([]);
		onProgramChange("");
		setOrgUnitIds([]);
		setPayerIds([]);
		setGroupLabelIds([]);
		setProviderId("");
		setStartDate("");
		setEndDate("");
		setCinInput("");
		setCin("");
		setPageNumber(0);
		setSelected(null);
	};

	// ---- report chrome — plain header card matching the Enrollment Summary
	// report (no brand band); PDF export keeps the full branded layout -------
	const SectionHead: FC<{ title: string; sub?: string }> = ({ title, sub }) => (
		<Box
			sx={{
				display: "flex",
				alignItems: "baseline",
				justifyContent: "space-between",
				borderBottom: `2px solid ${c.teal}`,
				pb: 0.75,
				mb: 2,
				gap: 2,
			}}
		>
			<Typography sx={{ fontSize: 16, fontWeight: 700, color: c.bandDark }}>
				{title}
			</Typography>
			{sub && (
				<Typography sx={{ fontSize: 12, color: c.muted, whiteSpace: "nowrap" }}>
					{sub}
				</Typography>
			)}
		</Box>
	);

	const ReportHeader: FC = () => (
		<Box
			sx={{
				display: "flex",
				alignItems: "flex-start",
				justifyContent: "space-between",
				flexWrap: "wrap",
				gap: 2,
			}}
		>
			<Box>
				<Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
					Visit Detail Report
				</Typography>
				<Typography sx={{ fontSize: 15, color: c.muted, mt: 0.75 }}>
					Reports / Visit detail
				</Typography>
				<Typography sx={{ fontSize: 15, color: c.muted, mt: 1 }}>
					Generated {generated}
				</Typography>
			</Box>
			<Stack direction="row" spacing={2}>
				<Button
					variant="outlined"
					startIcon={
						exporting === "excel" ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							<FileDownloadOutlinedIcon />
						)
					}
					disabled={exporting !== ""}
					onClick={handleExcel}
				>
					{exporting === "excel" ? "Exporting…" : "Excel"}
				</Button>
				<Button
					variant="outlined"
					startIcon={
						exporting === "pdf" ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							<FileDownloadOutlinedIcon />
						)
					}
					disabled={exporting !== ""}
					onClick={handlePdf}
				>
					{exporting === "pdf" ? "Exporting…" : "PDF"}
				</Button>
			</Stack>
		</Box>
	);

	const Footnote: FC = () => (
		<Typography sx={{ fontSize: 11, color: c.muted, mt: 1.5, lineHeight: 1.5 }}>
			Each analyte generates a separate row in the Excel export, with visit
			fields repeated per analyte (Visit date, Member, CIN, Event, Result,
			Earned, Payer plan, Cup, Tampered, Short name, Scientific name, Analyte
			result). Visits without cup analytes export a single row.
		</Typography>
	);

	/**
	 * SIMS-8907 — the analyte detail renders as an EXPANDED ROW under the visit
	 * it belongs to, per the design, instead of as a separate table below the
	 * results. Built here and rendered from inside the table body so the panel
	 * sits in the same <tbody> as its row; with a long page of visits, a panel
	 * detached from its row left the reader scrolling to work out which visit
	 * they had opened.
	 *
	 * The JSX below is unchanged from when it lived after the results card — only
	 * its position moved.
	 */
	const analytePanel = selected ? (
		<Box>
			{/* Teal section rule + two cards (meta + results). The heading itself is
			    the cup panel name — see SIMS-8906 below; this comment used to quote
			    the old `member ?? "Unknown"` title and contradicted the code. */}
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					borderBottom: `2px solid ${c.teal}`,
					pb: 0.75,
					mb: 2,
					gap: 2,
				}}
			>
				<Box>
					<Typography sx={{ fontSize: 16, fontWeight: 700, color: c.bandDark }}>
						{/*
						 * SIMS-8906 — head the panel with the CUP PANEL name, which is
						 * what it is a panel OF.
						 *
						 * This was `${member} Analytes`, so an account that masks
						 * beneficiary names (`displayBeneficiaryName: false`, sending
						 * `member: null` on purpose — see SIMS-8900) produced "Unknown
						 * Analytes" over a urine screen listing five positives. On a
						 * clinical document that reads as "the substances were not
						 * identified" when all thirteen are named directly below, which
						 * is why it is worth more than a cosmetic fix.
						 *
						 * The cup name is already resolved for the meta card, so the
						 * title now uses the same value rather than a second source.
						 * The participant stays identified by the CIN line below.
						 */}
						{analyteQuery.data?.cupName || "Analytes"}
					</Typography>
					<Typography sx={{ fontSize: 12, color: c.muted, mt: 0.25 }}>
						{[
							`CIN ${selected.cin ?? "—"}`,
							selected.eventName,
							selected.visitDate
								? dayjs(selected.visitDate).format(dateFormat)
								: null,
						]
							.filter(Boolean)
							.join(" · ")}
					</Typography>
				</Box>
				<IconButton
					size="small"
					onClick={() => setSelected(null)}
					aria-label="Close analyte details"
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			{analyteQuery.isLoading ? (
				<LinearProgress />
			) : (
				<Stack spacing={2}>
					{/* Card 1 — visit meta */}
					<Paper
						variant="outlined"
						sx={{ borderColor: c.border, borderRadius: 3, p: 2.5 }}
					>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
							{summaryField("Cup Name", analyteQuery.data?.cupName ?? "—")}
							{summaryField(
								"Tampered",
								analyteQuery.data?.isTampered ? (
									<Chip
										size="small"
										color="warning"
										label={
											analyteQuery.data.tamperedReasons.length
												? `Yes — ${analyteQuery.data.tamperedReasons.join(", ")}`
												: "Yes"
										}
									/>
								) : (
									<Chip
										size="small"
										label="No"
										sx={{
											bgcolor: theme.palette.secondary.light,
											color: theme.palette.secondary.dark,
										}}
									/>
								),
							)}
							{summaryField(
								"Visit Result",
								<ResultPill
									value={
										selAnalyte
											? selAnalyte.positive
												? "Positive"
												: "Negative"
											: selected.result
									}
									color={selAnalyte ? null : selected.resultColor}
								/>,
							)}
							{summaryField("Payer Plan", selected.payerPlanName ?? "—")}
						</Box>
					</Paper>

					{/* Card 2 — analyte results */}
					<Paper
						variant="outlined"
						sx={{
							borderColor: c.border,
							borderRadius: 3,
							overflow: "hidden",
						}}
					>
						<Box sx={{ overflowX: "auto" }}>
							<Box
								component="table"
								sx={{
									/*
									 * SIMS-8907 side effect, fixed here: this table has THREE
									 * columns but now lives in a full-width row of the visits
									 * table, which is ~1700px wide. At width:100% the three
									 * columns were stretched across all of it, with the
									 * right-aligned result column thrown to the far edge —
									 * metres away from the analyte it belongs to. Before the
									 * panel moved inside the table it was bounded by the card.
									 *
									 * Capped and column-sized so the three read as a group. Sized
									 * from the real content: "AMP"-style codes, scientific names
									 * up to "Phencyclidine", and POSITIVE/NEGATIVE.
									 */
									width: "100%",
									maxWidth: 620,
									tableLayout: "fixed",
									borderCollapse: "collapse",
								}}
							>
								<colgroup>
									<col style={{ width: 130 }} />
									<col style={{ width: 250 }} />
									<col style={{ width: 140 }} />
								</colgroup>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											textAlign: "left",
											color: c.muted,
											borderBottom: `1px solid ${c.divider}`,
										}}
									>
										{["Short name", "Scientific name", "Analyte result"].map(
											(h, i) => (
												<Box
													key={h}
													component="th"
													sx={{
														fontSize: 12,
														fontWeight: 600,
														letterSpacing: "0.05em",
														// 24px of side padding on a 620px table left the
														// columns further apart than the values are wide.
														p: "14px 12px",
														textAlign: i === 2 ? "right" : "left",
														textTransform: "uppercase",
													}}
												>
													{h}
												</Box>
											),
										)}
									</Box>
								</Box>
								<Box component="tbody">
									{analytes.length === 0 ? (
										<Box component="tr">
											<Box
												component="td"
												colSpan={3}
												sx={{ p: "18px 12px", color: c.muted }}
											>
												No analytes recorded for this visit.
											</Box>
										</Box>
									) : (
										analytes.map((a, i) => (
											<Box
												component="tr"
												key={`${a.shortName ?? "analyte"}-${i}`}
												onClick={() => setAnalyteIdx(i)}
												sx={{
													borderBottom: `1px solid ${c.divider}`,
													// Selected row is clearly filled (incl. negatives);
													// positives keep their red tint.
													bgcolor: a.positive
														? c.posBg
														: i === analyteIdx
															? c.band
															: "transparent",
													cursor: "pointer",
													boxShadow:
														i === analyteIdx
															? `inset 3px 0 0 ${c.accent}`
															: "none",
													"&:hover": {
														bgcolor: a.positive ? c.posBg : c.band,
													},
												}}
											>
												<Box
													component="td"
													sx={{
														p: "18px 12px",
														fontWeight:
															a.positive || i === analyteIdx ? 700 : 400,
													}}
												>
													{a.shortName ?? "—"}
												</Box>
												<Box
													component="td"
													sx={{
														p: "18px 12px",
														fontWeight: a.positive ? 700 : 400,
													}}
												>
													{a.scientificName ?? "—"}
												</Box>
												<Box
													component="td"
													sx={{ p: "18px 12px", textAlign: "right" }}
												>
													<Box
														component="span"
														sx={{
															fontSize: 13,
															fontWeight: 700,
															letterSpacing: "0.04em",
															color: a.positive ? c.posInk : c.negInk,
														}}
													>
														{a.positive ? "POSITIVE" : "NEGATIVE"}
													</Box>
												</Box>
											</Box>
										))
									)}
								</Box>
							</Box>
						</Box>
					</Paper>
				</Stack>
			)}
		</Box>
	) : null;

	return (
		<Stack spacing={3}>
			{/* Report header: plain title card + outlined export buttons (matches
			    the Enrollment Summary report) */}
			<Paper
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<ReportHeader />
			</Paper>

			{/* Filter bar card */}
			<Paper
				ref={filterBarRef}
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2,1fr)",
							// Four equal columns, matching Enrollment Summary.
							md: "repeat(4,1fr)",
						},
						gap: 3,
						alignItems: "end",
					}}
				>
					<ProgramFilterField
						programIds={programIds ?? []}
						programs={programs}
						onProgramsChange={(ids) => {
							onProgramsChange?.(ids);
							setPageNumber(0);
							setSelected(null);
							// Org units, payers, group labels and provider are all
							// program-specific — clear every scope selection so a stale
							// value from the previous program isn't sent (e.g. an OU that
							// doesn't belong to the newly selected program).
							setOrgUnitIds([]);
							setPayerIds([]);
							setGroupLabelIds([]);
							setProviderId("");
						}}
						labelColor={c.label}
						mutedColor={c.muted}
						errorColor={theme.palette.error.main}
					/>
					{/* Grouped panel (SIMS-7994 AC2): shared org units first, with counts. */}
					<OrgUnitFilterField
						userPrograms={userInfo?.programs}
						selectedProgramIds={programIds ?? []}
						programs={programs}
						options={siteOptions}
						value={orgUnitIds}
						onChange={changeOrgUnitIds}
						labelColor={c.label}
						mutedColor={c.muted}
					/>
					{/* Sub-labels from the hand-off — options span the selected programs. */}
					<FilterField label="Payer" helperText="Grouped by program">
						{multiInput(
							payerIds,
							payerOptions,
							changePayerIds,
							payersDisabled ? "No payers for this program" : "All payers",
							payersDisabled,
						)}
					</FilterField>
					<FilterField label="Group labels" helperText="Grouped by program">
						{multiInput(
							groupLabelIds,
							groupLabelOptions,
							changeGroupLabelIds,
							"All",
						)}
					</FilterField>
					<FilterField label="Provider">
						<Select
							size="small"
							displayEmpty
							value={providerId}
							input={<OutlinedInput />}
							onChange={(e) => changeProviderId(e.target.value)}
							renderValue={(v) =>
								v ? (
									(providerOptions.find((o) => o.id === v)?.name ?? v)
								) : (
									<Typography sx={{ color: c.muted }}>All providers</Typography>
								)
							}
						>
							<MenuItem value="">All providers</MenuItem>
							{providerOptions.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name ?? o.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>
					{/* newRow: the dates start a fresh row, per the requested layout. */}
					<FilterField label="Start date" newRow>
						{/* SIMS-7770 (#5): MUI-X picker accepts a typed 4-digit year.
						    disableFuture={false} preserves the old native input's behavior
						    (future dates allowed); isValid() guards the typed-invalid state. */}
						<BasicDatepicker
							label="Start date"
							format={dateFormat}
							disableFuture={false}
							value={startDate ? dayjs(startDate) : null}
							onChange={(d) =>
								changeStartDate(d && d.isValid() ? d.format("YYYY-MM-DD") : "")
							}
						/>
					</FilterField>
					<FilterField label="End date">
						<BasicDatepicker
							label="End date"
							format={dateFormat}
							disableFuture={false}
							value={endDate ? dayjs(endDate) : null}
							onChange={(d) =>
								changeEndDate(d && d.isValid() ? d.format("YYYY-MM-DD") : "")
							}
						/>
					</FilterField>
					<FilterField label="CIN" badge="NEW">
						<TextField
							size="small"
							placeholder="Search by CIN"
							value={cinInput}
							onChange={(e) => setCinInput(e.target.value)}
							onBlur={applyCin}
							onKeyDown={(e) => {
								if (e.key === "Enter") applyCin();
							}}
						/>
					</FilterField>
					{/* Clear all + Run report — last cell, right-aligned (per handoff) */}
					{/* Two grid cells — see ReportActions. */}
					<ReportActions
						onClearAll={() => {
							clearFilters();
							setApplied(null);
						}}
						onRun={runReport}
						canRun={(programIds ?? []).length > 0}
					/>
				</Box>
			</Paper>

			{/* Reserve the bar's height so the table doesn't jump on refetch. */}
			<Box sx={{ height: 4 }}>
				{(isLoading || isFetching) && <LinearProgress />}
			</Box>

			{/* Recomputed from the live selection; hidden when nothing is excluded. */}
			{/*
			 * SIMS-8006 AC1: the SERVER's count of programs that contributed nothing
			 * after narrowing. `excludedProgramCount` remains only as the fallback for
			 * an API predating SIMS-8014 — it knows org-unit exclusion alone and misses
			 * every other zero-row cause, so it under-reports.
			 */}
			<ExclusionBanner
				hiddenCount={hiddenProgramCount(
					data?.programFilters,
					excludedProgramCount,
				)}
				selectedCount={(programIds ?? []).length}
				onViewPrograms={viewPrograms}
			/>

			{(programIds ?? []).length === 0 && (
				<NoProgramCard borderColor={c.border} mutedColor={c.muted} />
			)}

			{/* AC1: with a selection but no run yet, prompt instead of an empty table. */}
			{(programIds ?? []).length > 0 && !applied && (
				<Paper
					variant="outlined"
					sx={{
						borderColor: c.border,
						borderRadius: 3,
						py: 9,
						px: 3,
						textAlign: "center",
					}}
				>
					<Typography sx={{ fontSize: 20, mt: 1 }}>
						Select Run report to generate results
					</Typography>
					<Typography sx={{ color: c.muted, mt: 1 }}>
						All programs you are assigned to and the default filters are
						pre-selected. Changing any filter clears the results.
					</Typography>
				</Paper>
			)}

			{applied && (
				<>
					{/* Visit results card */}
					<Paper
						variant="outlined"
						sx={{
							borderColor: c.border,
							borderRadius: 3,
							overflow: "hidden",
							minWidth: 0,
						}}
					>
						<Box sx={{ px: 3, pt: 2.5 }}>
							{/* SIMS-8078 (AC1): the caption is the only place that states how
							    many programs contributed to the merged set, so it carries the
							    "all their visits in one table" half of AC1 in words, plus the
							    sort the Hi-Fi states ("N programs · N visits · sorted by visit
							    date"). The existing page-range text is KEPT rather than
							    replaced — the design omits it, but removing information users
							    have today is exactly what the column ruling declined to do. */}
							<SectionHead
								title="Visits"
								sub={`${programCount} ${
									programCount === 1 ? "program" : "programs"
								} · ${rangeStart}–${rangeEnd} of ${total} visits · sorted by visit date`}
							/>
						</Box>

						{/* Plain table (B1 handoff): no selection — a "See details"
						    action drives the drill-down. Rows are clickable to open
						    the two detail cards; a totals row sums Earned. */}
						<Box sx={{ px: 3, pb: 1, overflowX: "auto" }}>
							<Box
								component="table"
								sx={{
									width: "100%",
									minWidth: TABLE_MIN_WIDTH,
									borderCollapse: "collapse",
									tableLayout: "fixed",
									fontSize: 14,
								}}
							>
								{/* Fixed layout + explicit column widths: long Member/Org/
								    Provider/Payer values truncate with an ellipsis instead of
								    widening their column and pushing the Actions column off the
								    right edge. The card scrolls horizontally when too narrow. */}
								{/* SIMS-7989: one <col> PER COLUMN — the count must equal HEAD's.
								    With tableLayout:"fixed" a column with no <col> gets only the
								    leftover width. The declared widths sum to TABLE_MIN_WIDTH, so
								    at that width there IS no leftover: the undeclared columns
								    collapse to ~0 and render on top of each other. This was
								    already latent (12 columns against 10 <col>); adding Program
								    and Enrollment Status turned 2 starved columns into 4 and made
								    it visible. COL_WIDTHS is now the single source of truth for
								    both the <col> list and the table's minWidth so they cannot
								    drift apart, and a test asserts the count matches HEAD. */}
								<Box component="colgroup">
									{COL_WIDTHS.map((w, i) => (
										<Box
											component="col"
											key={HEAD[i]?.id ?? `col-${i}`}
											sx={{ width: w }}
										/>
									))}
								</Box>
								<Box component="thead">
									<Box
										component="tr"
										sx={{ borderBottom: `2px solid ${c.divider}` }}
									>
										{HEAD.map((h) => (
											<Box
												key={h.id}
												component="th"
												sx={{
													p: "10px 12px",
													fontSize: 12,
													fontWeight: 700,
													letterSpacing: "0.04em",
													textTransform: "uppercase",
													verticalAlign: "bottom",
													color: c.muted,
													textAlign: h.align === "right" ? "right" : "left",
												}}
											>
												{h.label}
											</Box>
										))}
									</Box>
								</Box>
								<Box component="tbody">
									{isLoading || isFetching ? (
										<Box component="tr">
											<Box component="td" colSpan={HEAD.length} sx={{ p: 3 }}>
												<LinearProgress />
											</Box>
										</Box>
									) : visitRows.length === 0 ? (
										<Box component="tr">
											<Box
												component="td"
												colSpan={HEAD.length}
												sx={{ p: 4, textAlign: "center", color: c.muted }}
											>
												No visits match the current filters.
											</Box>
										</Box>
									) : (
										<>
											{visitRows.map((r) => {
												const isOpen = selected?.udtResultId === r.udtResultId;
												const clickable = r.hasAnalyteDetails;
												return (
													// SIMS-8907: the row and its analyte panel are siblings in
													// the same <tbody>, so the key moves to the fragment.
													<Fragment key={r.udtResultId}>
														<Box
															component="tr"
															onClick={() => selectVisit(r)}
															sx={{
																borderBottom: `1px solid ${c.divider}`,
																bgcolor: isOpen ? c.band : "transparent",
																cursor: clickable ? "pointer" : "default",
																"&:hover": clickable
																	? { bgcolor: c.band }
																	: undefined,
															}}
														>
															<Box
																component="td"
																sx={{ p: "10px 12px", whiteSpace: "nowrap" }}
															>
																{r.visitDate
																	? dayjs(r.visitDate).format(dateFormat)
																	: "—"}
															</Box>
															<Box
																component="td"
																sx={{
																	p: "10px 12px",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{r.member ?? "—"}
															</Box>
															<Box
																component="td"
																sx={{ p: "10px 12px", whiteSpace: "nowrap" }}
															>
																{r.cin ?? "—"}
															</Box>
															<Box
																component="td"
																sx={{
																	p: "10px 12px",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{r.orgUnit ?? "—"}
															</Box>
															<Box
																component="td"
																sx={{
																	p: "10px 12px",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{r.provider ?? "—"}
															</Box>
															<Box
																component="td"
																sx={{
																	p: "10px 12px",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{r.eventName ?? "—"}
															</Box>
															<Box
																component="td"
																sx={{ p: "10px 12px", whiteSpace: "nowrap" }}
															>
																{r.visitNumber ?? "—"}
															</Box>
															<Box component="td" sx={{ p: "10px 12px" }}>
																<ResultPill
																	value={r.result}
																	color={r.resultColor}
																/>
															</Box>
															<Box
																component="td"
																sx={{
																	p: "10px 12px",
																	textAlign: "right",
																	whiteSpace: "nowrap",
																}}
															>
																{money(r.earned)}
															</Box>
															<Box
																component="td"
																sx={{
																	p: "10px 12px",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{r.payerPlanName ?? "—"}
															</Box>
															<Box
																component="td"
																sx={{
																	p: "10px 12px",
																	whiteSpace: "nowrap",
																}}
															>
																{r.payerPlanNumber ?? "—"}
															</Box>
															{/* SIMS-8078 (AC1): the Program this row belongs to, rendered
														    as a pill per the Hi-Fi (brand text on a tinted brand
														    surface, fully rounded). The FULL program name is shown and
														    the cell ellipsizes — the mock abbreviates ("Northside
														    Div.") but states no truncation rule, so inventing an
														    abbreviation scheme would be inventing design. `title`
														    exposes the full value when it is clipped. */}
															<Box component="td" sx={{ p: "10px 12px" }}>
																{r.programName ? (
																	<Box
																		component="span"
																		title={r.programName}
																		sx={{
																			display: "inline-block",
																			maxWidth: "100%",
																			overflow: "hidden",
																			whiteSpace: "nowrap",
																			textOverflow: "ellipsis",
																			verticalAlign: "middle",
																			px: "10px",
																			py: "5px",
																			borderRadius: 999,
																			fontSize: 12,
																			fontWeight: 500,
																			color: c.accent,
																			bgcolor: c.band,
																		}}
																	>
																		{r.programName}
																	</Box>
																) : (
																	"—"
																)}
															</Box>
															<Box
																component="td"
																sx={{
																	p: "10px 12px",
																	whiteSpace: "nowrap",
																	textAlign: "right",
																}}
															>
																{r.hasAnalyteDetails ? (
																	<Box
																		component="span"
																		sx={{
																			color: c.accent,
																			textDecoration: "underline",
																			fontWeight: isOpen ? 700 : 500,
																		}}
																	>
																		{/* SIMS-5672: label toggles See Details / Hide */}
																		{isOpen ? "Hide" : "See Details"}
																	</Box>
																) : null}
															</Box>
														</Box>
														{/*
														 * SIMS-8907 — the analyte detail belongs UNDER its visit, per
														 * the design, not in a separate table below the results. It
														 * spans the full row so the panel keeps its own layout, and
														 * it is only mounted for the open row, so exactly one panel
														 * exists at a time (`selected` already enforces that).
														 */}
														{isOpen && analytePanel ? (
															<Box component="tr">
																<Box
																	component="td"
																	colSpan={HEAD.length}
																	sx={{ p: 0, bgcolor: c.band }}
																>
																	{analytePanel}
																</Box>
															</Box>
														) : null}
													</Fragment>
												);
											})}
											{/* SIMS-7770 (B1): Totals reflect the WHOLE filtered set (all
											    pages), from the server — not just the current page. */}
											<Box
												component="tr"
												sx={{
													bgcolor: c.band,
													borderTop: `2px solid ${c.divider}`,
												}}
											>
												<Box
													component="td"
													colSpan={8}
													sx={{
														p: "10px 12px",
														fontWeight: 700,
														color: c.bandDark,
													}}
												>
													{/* SIMS-8091 (AC1): the Hi-Fi's label for this band, which names
													    what the figure actually spans — every SELECTED program, over
													    the whole filtered set, not this page. The visit count is kept
													    rather than dropped (the design omits it), for the same reason
													    the caption kept its page range: removing information users
													    have today is what the column ruling declined to do.
													    The band renders only when there are rows — it sits in the
													    else-branch of the empty-state check — which is exactly the
													    zero-row behaviour agreed at kickoff Q4=A, since a $0.00 total
													    across "all selected programs" would read as a data error. */}
													{`All selected programs — grand total · ${total} ${
														total === 1 ? "visit" : "visits"
													}`}
												</Box>
												<Box
													component="td"
													sx={{
														p: "10px 12px",
														textAlign: "right",
														fontWeight: 700,
														color: c.bandDark,
													}}
												>
													{money(earnedTotal)}
												</Box>
												{/* SIMS-8078: trailing filler keeps the Incentive Earned figure
												    aligned under its own column. It now spans Payer Plan, Payer
												    Plan ID, the new Program column and Actions — 4, not 2. (The
												    pre-SIMS-8078 value of 2 left the row one cell short of the
												    12-column header; browsers tolerate it, but the alignment is
												    only correct by accident, so it is fixed here rather than
												    extended.) SIMS-8091 restyles this row into the
												    "All selected programs — grand total" band. */}
												<Box component="td" colSpan={5} />
											</Box>
										</>
									)}
								</Box>
							</Box>
						</Box>

						{/* Pager — mirrors EnrollmentSummary (count + prev/next). */}
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{
								px: 3,
								py: 1.5,
								borderTop: `1px solid ${c.divider}`,
							}}
						>
							<Typography sx={{ fontSize: 14, color: c.muted }}>
								{`${rangeStart}–${rangeEnd} of ${total} visits`}
							</Typography>
							<Stack direction="row" spacing={1} alignItems="center">
								<Button
									size="small"
									variant="outlined"
									disabled={pageNumber === 0}
									onClick={() => setPageNumber((p) => Math.max(0, p - 1))}
									sx={{ color: c.ink, borderColor: c.border }}
								>
									Prev
								</Button>
								<Typography sx={{ fontSize: 14, color: c.muted }}>
									{`Page ${pageNumber + 1} of ${Math.max(
										1,
										Math.ceil(total / pageSize),
									)}`}
								</Typography>
								<Button
									size="small"
									variant="outlined"
									disabled={rangeEnd >= total}
									onClick={() => setPageNumber((p) => p + 1)}
									sx={{ color: c.ink, borderColor: c.border }}
								>
									Next
								</Button>
							</Stack>
						</Stack>
					</Paper>

					<Footnote />

					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						sx={{ pt: 1.5, borderTop: `1px solid ${c.border}` }}
					>
						<Typography sx={{ fontSize: 11, color: c.muted }}>
							{[programName, brand?.title].filter(Boolean).join(" — ")}
						</Typography>
						<Typography
							sx={{
								fontSize: 11,
								fontWeight: 700,
								color: c.bandDark,
								letterSpacing: "0.08em",
							}}
						>
							{brand?.title || "SIMS"}
						</Typography>
						<Typography sx={{ fontSize: 11, color: c.muted }}>
							Generated {generated}
						</Typography>
					</Stack>
				</>
			)}
		</Stack>
	);
};

export default VisitDetailTab;
